import { Link } from "react-router-dom";

import type { CardOut } from "@/api/cards";
import type { KpiEntry } from "@/api/kpi";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

interface YorumOzetKartiProps {
  card: CardOut | undefined;
  entry: KpiEntry | undefined;
  onOpenDetail: () => void;
}

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/** `subitems.dusuk_puan.count` — TÜM zamanların 1-2⭐ yorum sayısı. */
function dusukPuanAdedi(entry: KpiEntry | undefined): number {
  const subitems = entry?.subitems;
  if (!subitems || typeof subitems !== "object") return 0;
  const dusukPuan = (subitems as Record<string, unknown>).dusuk_puan;
  if (!dusukPuan || typeof dusukPuan !== "object") return 0;
  return n((dusukPuan as Record<string, unknown>).count);
}

/**
 * Müşteri yorumları özet kartı — `.kpi` kart; tıklanınca detay modali,
 * sağ üstte "Yorumlar sekmesi" bağlantısı.
 */
export function YorumOzetKarti({ card, entry, onOpenDetail }: YorumOzetKartiProps) {
  if (!card) return null;

  const state = card.state;
  const count = n(entry?.count);
  const ortalama = entry?.ortalama_puan != null ? Number(entry.ortalama_puan) : null;
  const dusuk = dusukPuanAdedi(entry);
  const onayBekleyen = n((entry as Record<string, unknown> | undefined)?.onay_bekleyen_sayisi);
  const tone = state !== "live" ? "kpi-off" : dusuk > 0 ? "kpi-warn" : "kpi-ok";
  const clickable = state === "live";

  return (
    <div
      className={`kpi ${tone} ${clickable ? "kpi-clickable" : ""}`}
      data-testid="yorum-ozet-karti"
      onClick={clickable ? onOpenDetail : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenDetail();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="kpi-lbl">{t("ozet.yorum_ozet.baslik")}</h3>
        <Link to="/yorumlar" onClick={(e) => e.stopPropagation()} className="btn-link !min-h-0 shrink-0 text-xs">
          {t("ozet.yorum_ozet.sekme_link")}
        </Link>
      </div>

      {state === "live" ? (
        <div className="mt-2">
          <p className="kpi-val">{count > 0 ? `${fmtInt(count)} · ${ortalama != null ? ortalama.toFixed(1) : "—"}⭐` : "0"}</p>
          <p className="kpi-sub">
            {count === 0 ? t("ozet.yorum_ozet.hic_yok") : dusuk > 0 ? t("ozet.yorum_ozet.dusuk_uyari", { n: String(dusuk) }) : t("ozet.yorum_ozet.hepsi_olumlu")}
          </p>
          {onayBekleyen > 0 && (
            <p className="mt-1 text-xs font-semibold text-amber" data-testid="yorum-onay-bekleyen">
              {t("ozet.yorum_ozet.onay_bekleyen", { n: fmtInt(onayBekleyen) })}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted">{card.reason ? t(`card.reason.${card.reason}`) : t(`empty.${state}.body`)}</p>
      )}
    </div>
  );
}
